import { useEffect, useMemo, useState } from "react";

import {

  Alert,

  Autocomplete,
  createFilterOptions,

  Box,

  Button,

  Card,

  CardContent,

  Chip,

  Link,

  Stack,

  Table,

  TableBody,

  TableCell,

  TableContainer,

  TableHead,

  TableRow,

  Typography,

  Paper,

  ToggleButton,

  ToggleButtonGroup,

  TextField,

} from "@mui/material";

import LinkIcon from "@mui/icons-material/Link";

import StarIcon from "@mui/icons-material/Star";

import ViewListIcon from "@mui/icons-material/ViewList";

import GroupIcon from "@mui/icons-material/Group";

import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import { useAuthStore } from "../../store/authStore";

import {

  formatPhoneDisplay,

  normPhoneDigits,

  whatsappHref,

} from "../../utils/phoneCountryCodes";
import { catalogVisitorKey } from "../../utils/catalogVisitor";

const normWa = (v) => normPhoneDigits(v);

const visitorKey = (interes) => catalogVisitorKey(interes);

const visitorDisplayName = (interes) => {
  const nombre = String(interes?.shopperNombre || "").trim();
  const apellido = String(interes?.shopperApellido || "").trim();
  return [nombre, apellido].filter(Boolean).join(" ");
};



const sugiereCoincidencia = (interes, cliente) => {

  if (interes.cliente_id) return false;

  const waI = normWa(interes.shopperWhatsapp);

  const waC = normWa(cliente.whatsapp);

  if (waI && waC && waI === waC) return true;

  const mailI = String(interes.shopperEmail || "").trim().toLowerCase();

  const mailC = String(cliente.email || cliente.correo || "").trim().toLowerCase();

  if (mailI && mailC && mailI === mailC) return true;

  return false;

};



function VisitorContact({ interes, cliente = null }) {
  const phoneRaw = interes.shopperWhatsapp || cliente?.whatsapp || "";
  const href = whatsappHref(phoneRaw);
  const display = formatPhoneDisplay(phoneRaw) || phoneRaw;
  const fullName = visitorDisplayName(interes);

  return (

    <Box>

      {fullName ? (
        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.25 }}>
          {fullName}
        </Typography>
      ) : null}
      <Typography
        variant="body2"
        color={fullName ? "text.secondary" : "text.primary"}
        sx={{ fontSize: fullName ? "0.8125rem" : undefined }}
      >
        {interes.shopperEmail || "—"}
      </Typography>

      {phoneRaw ? (

        href ? (

          <Link

            href={href}

            target="_blank"

            rel="noopener noreferrer"

            variant="caption"

            sx={{

              display: "inline-flex",

              alignItems: "center",

              gap: 0.35,

              fontWeight: 600,

            }}

            onClick={(e) => e.stopPropagation()}

          >

            <WhatsAppIcon sx={{ fontSize: 14, color: "#25D366" }} />

            {display}

          </Link>

        ) : (

          <Typography variant="caption" color="text.secondary">

            WA {display}

          </Typography>

        )

      ) : null}

    </Box>

  );

}



const clienteSearchHaystack = (c) => {
  const wa = c?.whatsapp ? formatPhoneDisplay(c.whatsapp) || c.whatsapp : "";
  return [
    c?.nombre,
    c?.email,
    c?.correo,
    wa,
    normWa(c?.whatsapp),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

const matchClienteSearch = (c, inputValue) => {
  const q = String(inputValue || "").trim().toLowerCase();
  if (!q) return true;
  const hay = clienteSearchHaystack(c);
  const qDigits = normWa(q);
  if (qDigits.length >= 3 && normWa(c?.whatsapp).includes(qDigits)) return true;
  const tokens = q.split(/\s+/).filter(Boolean);
  return tokens.every((t) => {
    const tDigits = normWa(t);
    if (tDigits.length >= 3 && hay.replace(/\D/g, "").includes(tDigits)) return true;
    return hay.includes(t);
  });
};

const filterClientesOptions = createFilterOptions({
  stringify: (c) => clienteSearchHaystack(c),
  ignoreCase: true,
  limit: 80,
});

function ClienteLinkPicker({ clientes, value, onChange, sugeridos = [] }) {
  const [inputValue, setInputValue] = useState("");

  const options = useMemo(() => {
    const ids = new Set();
    const list = [];
    for (const c of sugeridos) {
      if (c?.id && !ids.has(c.id)) {
        ids.add(c.id);
        list.push({ ...c, _sugerido: true });
      }
    }
    for (const c of clientes) {
      if (c?.id && !ids.has(c.id)) {
        ids.add(c.id);
        list.push(c);
      }
    }
    return list;
  }, [clientes, sugeridos]);

  const selected = options.find((c) => c.id === value) || null;

  useEffect(() => {
    if (!selected) {
      setInputValue("");
      return;
    }
    const wa = selected.whatsapp
      ? formatPhoneDisplay(selected.whatsapp) || selected.whatsapp
      : "";
    setInputValue(`${selected.nombre}${wa ? ` (${wa})` : ""}`);
  }, [selected?.id, selected?.nombre, selected?.whatsapp]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_, next, reason) => {
        if (reason === "reset" && selected) {
          const wa = selected.whatsapp
            ? formatPhoneDisplay(selected.whatsapp) || selected.whatsapp
            : "";
          setInputValue(`${selected.nombre}${wa ? ` (${wa})` : ""}`);
          return;
        }
        setInputValue(next);
      }}
      onChange={(_, c) => {
        onChange(c?.id || "");
        if (c) {
          const wa = c.whatsapp
            ? formatPhoneDisplay(c.whatsapp) || c.whatsapp
            : "";
          setInputValue(`${c.nombre}${wa ? ` (${wa})` : ""}`);
        } else {
          setInputValue("");
        }
      }}
      openOnFocus
      autoHighlight
      clearOnBlur={false}
      isOptionEqualToValue={(a, b) => a?.id === b?.id}
      noOptionsText={
        options.length === 0
          ? "No hay clientes en tu tienda — créalos arriba en CRM"
          : "Sin coincidencias — busca por nombre o WhatsApp del cliente"
      }
      getOptionLabel={(c) => {
        if (!c || typeof c !== "object") return "";
        const wa = c.whatsapp ? formatPhoneDisplay(c.whatsapp) || c.whatsapp : "sin WA";
        return `${c.nombre || "Cliente"} (${wa})`;
      }}
      filterOptions={(opts, state) => {
        const q = String(state.inputValue || "").trim();
        if (!q) return opts;
        const muiFiltered = filterClientesOptions(opts, state);
        if (muiFiltered.length > 0) return muiFiltered;
        return opts.filter((c) => matchClienteSearch(c, q));
      }}
      componentsProps={{
        popper: {
          sx: { zIndex: 1600 },
          placement: "bottom-start",
        },
      }}
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderOption={(props, c) => {
        const { key, ...rest } = props;
        return (
          <li key={key} {...rest}>
            <Stack>
              <Typography variant="body2" fontWeight={c._sugerido ? 700 : 500}>
                {c.nombre}
                {c._sugerido ? (
                  <Typography
                    component="span"
                    variant="caption"
                    color="warning.main"
                    sx={{ ml: 0.75 }}
                  >
                    sugerido
                  </Typography>
                ) : null}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[c.email || c.correo, c.whatsapp ? formatPhoneDisplay(c.whatsapp) : null]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Stack>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Nombre o WhatsApp del cliente de tienda"
          label="Cliente de tienda"
          helperText="No busca el correo del visitante del catálogo"
          FormHelperTextProps={{ sx: { mx: 0, mt: 0.5, fontSize: "0.7rem" } }}
        />
      )}
      sx={{ minWidth: { xs: "100%", sm: 300 }, maxWidth: 400 }}
    />
  );
}



export default function CatalogInteresesPanel({ store, clientes = [] }) {

  const user = useAuthStore((s) => s.user);

  const [feedback, setFeedback] = useState(null);

  const [linkingId, setLinkingId] = useState(null);

  const [pickCliente, setPickCliente] = useState("");

  const [viewMode, setViewMode] = useState("list");



  const intereses = store.interesesCatalogo || [];



  useEffect(() => {

    store.refreshInteresesCatalogo?.();

  }, []);



  const staffEmail = String(

    user?.email || user?.username || user?.correo || ""

  ).trim();



  const { pendientes, vinculados } = useMemo(() => {

    const p = [];

    const v = [];

    intereses.forEach((i) => {

      if (i.cliente_id) v.push(i);

      else p.push(i);

    });

    p.sort((a, b) => new Date(b.creado_at) - new Date(a.creado_at));

    v.sort((a, b) => new Date(b.creado_at) - new Date(a.creado_at));

    return { pendientes: p, vinculados: v };

  }, [intereses]);



  const clienteById = useMemo(() => {

    const m = {};

    clientes.forEach((c) => {

      m[c.id] = c;

    });

    return m;

  }, [clientes]);



  const groupIntereses = (items) => {

    const map = new Map();

    items.forEach((i) => {

      const key = visitorKey(i);

      if (!map.has(key)) {

        map.set(key, {

          key,

          email: i.shopperEmail,

          nombre: i.shopperNombre,

          apellido: i.shopperApellido,

          whatsapp: i.shopperWhatsapp,

          sample: i,

          items: [],

        });

      }

      map.get(key).items.push(i);

    });

    return [...map.values()].sort(

      (a, b) =>

        new Date(b.items[0]?.creado_at || 0) - new Date(a.items[0]?.creado_at || 0)

    );

  };



  const pendientesGrouped = useMemo(() => groupIntereses(pendientes), [pendientes]);

  const vinculadosGrouped = useMemo(() => groupIntereses(vinculados), [vinculados]);



  const handleVincular = async (interesId, clienteId) => {

    if (!clienteId) {

      setFeedback({ tipo: "warning", msg: "Selecciona un cliente de tu tienda" });

      return;

    }

    const result = await store.vincularInteresCatalogo({

      interes_id: interesId,

      cliente_id: clienteId,

      vinculado_por: staffEmail,

    });

    if (!result?.ok) {

      setFeedback({ tipo: "error", msg: result?.reason || "No se pudo vincular" });

      return;

    }

    const n = result?.linked_count || 1;
    setFeedback({
      tipo: "success",
      msg:
        n > 1
          ? `${n} intereses vinculados; los próximos "me interesa" de este visitante se asignarán al mismo cliente`
          : "Visitante vinculado al cliente; sus próximos intereses en el catálogo se vincularán solos",
    });

    setLinkingId(null);

    setPickCliente("");

  };



  const handleDesvincular = async (interesId) => {

    const result = await store.desvincularInteresCatalogo({ interes_id: interesId });

    if (!result?.ok) {

      setFeedback({ tipo: "error", msg: result?.reason || "No se pudo desvincular" });

      return;

    }

    setFeedback({ tipo: "info", msg: "Vínculo removido" });

  };



  if (intereses.length === 0) {

    return (

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>

        Aún no hay intereses guardados desde el catálogo público. Cuando un visitante marque

        productos con la estrella, aparecerán aquí para vincularlos manualmente a un cliente.

      </Alert>

    );

  }



  const renderLinkActions = (interes, sugeridos) => {

    const isLinking = linkingId === interes.id;

    if (interes.cliente_id) {

      return (

        <Button size="small" color="inherit" onClick={() => handleDesvincular(interes.id)}>

          Desvincular

        </Button>

      );

    }

    if (!isLinking) {

      return (

        <Button

          size="small"

          variant="outlined"

          startIcon={<LinkIcon />}

          onClick={() => {

            setLinkingId(interes.id);

            setPickCliente(sugeridos[0]?.id || "");

          }}

        >

          Vincular cliente

        </Button>

      );

    }

    return (

      <Stack spacing={0.75} alignItems="flex-end" sx={{ minWidth: 280 }}>

        <ClienteLinkPicker

          clientes={clientes}

          sugeridos={sugeridos}

          value={pickCliente}

          onChange={setPickCliente}

        />

        <Stack direction="row" spacing={0.5}>

          <Button

            size="small"

            variant="contained"

            onClick={() => handleVincular(interes.id, pickCliente)}

          >

            Confirmar

          </Button>

          <Button

            size="small"

            onClick={() => {

              setLinkingId(null);

              setPickCliente("");

            }}

          >

            Cancelar

          </Button>

        </Stack>

        {sugeridos.length > 0 && (

          <Button

            size="small"

            color="warning"

            onClick={() => {

              setPickCliente(sugeridos[0].id);

              handleVincular(interes.id, sugeridos[0].id);

            }}

          >

            Vincular sugerido: {sugeridos[0].nombre}

          </Button>

        )}

      </Stack>

    );

  };



  const renderRow = (interes, showActions = true) => {

    const cliente = interes.cliente_id ? clienteById[interes.cliente_id] : null;

    const sugeridos = clientes.filter((c) => sugiereCoincidencia(interes, c));



    return (

      <TableRow key={interes.id} hover>

        <TableCell>

          <Typography fontWeight={600} variant="body2">

            {interes.productoNombre || "Producto"}

          </Typography>

          <Typography variant="caption" color="text.secondary">

            {[interes.talla, interes.color].filter(Boolean).join(" · ") ||

              interes.categoria ||

              "—"}

          </Typography>

        </TableCell>

        {viewMode === "list" && (

          <TableCell>

            <VisitorContact interes={interes} cliente={cliente} />

          </TableCell>

        )}

        <TableCell>

          <Typography variant="caption" color="text.secondary">

            {interes.creado_at

              ? new Date(interes.creado_at).toLocaleString("es", {

                  day: "2-digit",

                  month: "short",

                  hour: "2-digit",

                  minute: "2-digit",

                })

              : "—"}

          </Typography>

        </TableCell>

        <TableCell>

          {cliente ? (

            <Chip size="small" color="success" label={cliente.nombre} />

          ) : sugeridos.length > 0 ? (

            <Chip

              size="small"

              color="warning"

              variant="outlined"

              label={`${sugeridos.length} posible coincidencia`}

            />

          ) : (

            <Chip size="small" variant="outlined" label="Sin vincular" />

          )}

        </TableCell>

        <TableCell align="right">

          {showActions ? renderLinkActions(interes, sugeridos) : null}

        </TableCell>

      </TableRow>

    );

  };



  const renderGroupedSection = (groups, { showActions, title }) => {

    if (groups.length === 0) return null;

    return (

      <>

        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>

          {title}

        </Typography>

        <Stack spacing={2} sx={{ mb: 2 }}>

          {groups.map((group) => (

            <Paper key={group.key} variant="outlined" sx={{ overflow: "hidden" }}>

              <Box

                sx={{

                  px: 2,

                  py: 1.25,

                  bgcolor: "grey.50",

                  borderBottom: "1px solid",

                  borderColor: "divider",

                }}

              >

                <Stack

                  direction={{ xs: "column", sm: "row" }}

                  alignItems={{ sm: "center" }}

                  justifyContent="space-between"

                  spacing={0.5}

                >

                  <VisitorContact
                    interes={group.sample}
                    cliente={clienteById[group.sample.cliente_id]}
                  />

                  <Chip

                    size="small"

                    label={`${group.items.length} producto${group.items.length === 1 ? "" : "s"}`}

                  />

                </Stack>

              </Box>

              <Table size="small">

                <TableHead>

                  <TableRow sx={{ "& th": { fontWeight: 700 } }}>

                    <TableCell>Producto</TableCell>

                    <TableCell>Fecha</TableCell>

                    <TableCell>Estado</TableCell>

                    <TableCell align="right">Acción</TableCell>

                  </TableRow>

                </TableHead>

                <TableBody>

                  {group.items.map((i) => renderRow(i, showActions))}

                </TableBody>

              </Table>

            </Paper>

          ))}

        </Stack>

      </>

    );

  };



  const tableHead = (

    <TableRow sx={{ "& th": { fontWeight: 700 } }}>

      <TableCell>Producto</TableCell>

      {viewMode === "list" && <TableCell>Visitante</TableCell>}

      <TableCell>Fecha</TableCell>

      <TableCell>Estado</TableCell>

      <TableCell align="right">Acción</TableCell>

    </TableRow>

  );



  return (

    <Card variant="outlined" sx={{ mb: 3 }}>

      <CardContent>

        <Stack

          direction={{ xs: "column", sm: "row" }}

          alignItems={{ xs: "flex-start", sm: "center" }}

          justifyContent="space-between"

          spacing={1}

          mb={1}

        >

          <Stack direction="row" alignItems="center" spacing={1}>

            <StarIcon sx={{ color: "#f59e0b" }} />

            <Typography variant="h6" fontWeight={700}>

              Intereses del catálogo

            </Typography>

            <Chip size="small" label={`${pendientes.length} por vincular`} color="warning" />

          </Stack>

          <ToggleButtonGroup

            size="small"

            exclusive

            value={viewMode}

            onChange={(_, v) => v && setViewMode(v)}

          >

            <ToggleButton value="list">

              <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />

              Lista

            </ToggleButton>

            <ToggleButton value="grouped">

              <GroupIcon fontSize="small" sx={{ mr: 0.5 }} />

              Por visitante

            </ToggleButton>

          </ToggleButtonGroup>

        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>

          Zero trust: los intereses del catálogo no se fusionan solos con clientes. Vincúlalos

          manualmente al cobrar o hacer seguimiento.

        </Typography>



        {feedback && (

          <Alert severity={feedback.tipo} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>

            {feedback.msg}

          </Alert>

        )}



        {viewMode === "list" ? (

          <>

            {pendientes.length > 0 && (

              <>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>

                  Pendientes de vincular

                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>

                  <Table size="small">

                    <TableHead>{tableHead}</TableHead>

                    <TableBody>{pendientes.map((i) => renderRow(i))}</TableBody>

                  </Table>

                </TableContainer>

              </>

            )}

            {vinculados.length > 0 && (

              <>

                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>

                  Ya vinculados

                </Typography>

                <TableContainer component={Paper} variant="outlined">

                  <Table size="small">

                    <TableHead>{tableHead}</TableHead>

                    <TableBody>{vinculados.map((i) => renderRow(i, true))}</TableBody>

                  </Table>

                </TableContainer>

              </>

            )}

          </>

        ) : (

          <>

            {renderGroupedSection(pendientesGrouped, {

              title: "Pendientes de vincular",

              showActions: true,

            })}

            {renderGroupedSection(vinculadosGrouped, {

              title: "Ya vinculados",

              showActions: true,

            })}

          </>

        )}

      </CardContent>

    </Card>

  );

}


